// Feedback survey content + answer validation (pure; shared by the survey UI,
// its tests, and mirrored by firestore.rules feedbackResponses and the admin
// panel in Tonyte_website/src/feedbackModel.js — keep ids in sync).

export const SURVEY_MAX_OTHER = 200;
export const SURVEY_MAX_COMMENT = 1000;

export type SurveyQuestionId = 'primaryUse' | 'foundSomething' | 'hardestPart' | 'moreUseful';

export const SURVEY_QUESTIONS: Array<{ id: SurveyQuestionId; options: string[] }> = [
  { id: 'primaryUse', options: ['finding_events', 'making_plans', 'meeting_people', 'browsing'] },
  { id: 'foundSomething', options: ['often', 'sometimes', 'once', 'not_yet'] },
  { id: 'hardestPart', options: ['finding_events', 'finding_people_plans', 'understanding', 'bugs_slow', 'nothing'] },
  { id: 'moreUseful', options: ['more_events', 'more_people_plans', 'better_recommendations', 'easier_navigation', 'better_performance'] },
];
export const OTHER_CHOICE = 'other';

export type SurveyAnswer = { choice: string | null; other?: string };
export type SurveyAnswers = Partial<Record<SurveyQuestionId, SurveyAnswer>>;

export type SurveyResponsePayload = {
  surveyId: string;
  answers: Partial<Record<SurveyQuestionId, { choice: string; other?: string }>>;
  rating: number;
  comment: string;
  platform: 'ios' | 'android' | 'web';
  appVersion: string;
  appBuild: string;
};

const clean = (value: unknown, max: number) => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, max) : '');

/**
 * Builds the stored response: unanswered questions are omitted, "Other" text
 * is kept only with the "other" choice (trimmed to SURVEY_MAX_OTHER), the
 * rating must be an integer 1–5. Returns an error key instead when invalid.
 */
export function buildSurveyResponse(input: {
  surveyId: string;
  answers: SurveyAnswers;
  rating: number | null;
  comment: string;
  platform: string;
  appVersion?: string | null;
  appBuild?: string | number | null;
}): { payload: SurveyResponsePayload } | { error: 'rating_required' | 'invalid_platform' | 'invalid_survey' } {
  if (!input.surveyId || input.surveyId.length > 40) return { error: 'invalid_survey' };
  if (!Number.isInteger(input.rating) || (input.rating as number) < 1 || (input.rating as number) > 5) return { error: 'rating_required' };
  if (input.platform !== 'ios' && input.platform !== 'android' && input.platform !== 'web') return { error: 'invalid_platform' };
  const answers: SurveyResponsePayload['answers'] = {};
  SURVEY_QUESTIONS.forEach(question => {
    const answer = input.answers[question.id];
    if (!answer?.choice) return;
    if (answer.choice === OTHER_CHOICE) {
      const other = clean(answer.other, SURVEY_MAX_OTHER);
      answers[question.id] = other ? { choice: OTHER_CHOICE, other } : { choice: OTHER_CHOICE };
    } else if (question.options.includes(answer.choice)) {
      answers[question.id] = { choice: answer.choice };
    }
  });
  return {
    payload: {
      surveyId: input.surveyId,
      answers,
      rating: input.rating as number,
      comment: clean(input.comment, SURVEY_MAX_COMMENT),
      platform: input.platform,
      appVersion: clean(input.platform === 'web' ? 'web' : input.appVersion || '', 40),
      appBuild: clean(input.appBuild == null ? '' : String(input.appBuild), 20),
    },
  };
}
